import React, { useState, useEffect, useRef, useContext } from "react";
import classnames from "classnames";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useUserEmail from "@/shared/hooks/useUserEmail";

import { getUserId, setAfterLoginRedirectURL } from "@/api/localStorage";
import { menuItemI, leftNavigationItems, getRightNavigationItems, hideNavigationRoutes } from "./config";
import { LogoutRoute, LoginRoute } from "@/routes/auth";
import { useTheme } from "@/shared/styles/useTheme";
import AppContext from "@/App/context/AppContext";
import Icon from "@/shared/components/Icon/Icon";
import Styles from "./NavigationDesktop.module.scss";
import HeaderStyles from "../Header.module.scss";
import { useFlavour } from "@/shared/flavours/useFlavour";
import { isAllowedRAFRoute } from "@/shared/utils/utils";
import Button from "@/shared/components/Button/Button";
import ProfileButton from "../ProfileButton";
import { useQuery } from "react-query";
import { checkReferralEligibility } from "@/api/referral";

const NavigationItems = ({
  items,
  openIndex,
  setOpenIndex,
}: {
  items: menuItemI[];
  openIndex: number;
  setOpenIndex: (index: number) => void;
}) => {
  const { navbar, primaryColorBackground } = useTheme();
  const { features } = useFlavour();
  const { loggedIn } = useContext(AppContext);

  const isVisible = (hide?: string[]) => {
    if (!hide) return true;
    if (hide.includes("auth") && loggedIn) return false;
    if (hide.includes("anon") && !loggedIn) return false;
    if (hide.includes("ratgeber") && !features?.selectivePages.ratgeber) return false;
    if (hide.includes("topDeals") && !features?.selectivePages.topDeals) return false;
    if (hide.includes("reminders") && !features?.selectivePages.reminders) return false;
    if (hide.includes("freunde-werben-freunde")) {
      return isAllowedRAFRoute(location.pathname);
    }
    return true;
  };

  return (
    <>
      {items.map(
        (item, index) =>
          isVisible(item.hide) && (
            <div
              className={classnames(Styles.menuItem, HeaderStyles.headerLink, { [Styles.menuGroup]: item.children })}
              key={`menuItem_${index}`}
            >
              {!item.children ? (
                // eslint-disable-next-line
                item.label === "Freunde werben" ? (
                  <Link to={item.url ?? ""} className={navbar.item} onClick={() => setOpenIndex(-1)}>
                    <div className={Styles.freundeWerbenContainer}>
                      <div className={Styles.freundeWerben}>
                        <div className={Styles.freundeWerbenIcon}>
                          <Icon name="gift" size="18px" className={classnames(Styles.labelIcon)} />
                        </div>
                        <div className={Styles.freundeWerbenText}>{item.label}</div>
                      </div>
                    </div>
                  </Link>
                ) : (
                  <Link to={item.url ?? ""} className={navbar.item} onClick={() => setOpenIndex(-1)}>
                    <span>{item.label}</span>
                  </Link>
                )
              ) : (
                <div
                  className={classnames(Styles.dropMenu, {
                    [Styles.open]: openIndex === index,
                  })}
                  onMouseOver={() => setOpenIndex(index)}
                  onMouseLeave={() => setOpenIndex(-1)}
                >
                  <div className={classnames(Styles.label)}>
                    {item.label === "profile" ? (
                      <ProfileButton
                        className={classnames(Styles.profileButton, Styles.noCursor, primaryColorBackground)}
                      />
                    ) : item.label === "Hilfe & Kontakt" ? (
                      <>
                        <Icon name="helpOutline" size="22px" className={Styles.labelIcon} />
                        {item.label}
                      </>
                    ) : (
                      item.label
                    )}
                  </div>
                  <SubMenu item={item} index={index} isVisible={isVisible} />
                </div>
              )}
            </div>
          )
      )}
    </>
  );
};

const Navigation = () => {
  const { loggedIn } = useContext(AppContext);
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState(-1);
  const location = useLocation();
  const menuRef = useRef(null);
  const hideNavigation = hideNavigationRoutes.some((route) => location.pathname.includes(route));
  const userID = getUserId();
  const userEmail = useUserEmail();

  const { data: eligibilityData } = useQuery(["referral-eligibility", userID], () => checkReferralEligibility(userID), {
    enabled: !!userID,
  });
  const showReferral = eligibilityData?.showRef;

  // Close dropdown menu   when location changes
  useEffect(() => {
    if (openIndex !== -1) {
      setOpenIndex(-1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  return (
    <>
      <div className={Styles.navigation} ref={menuRef}>
        <NavigationItems
          items={hideNavigation ? [] : leftNavigationItems}
          openIndex={openIndex}
          setOpenIndex={setOpenIndex}
        />
      </div>
      <div className={Styles.navigation}>
        <>
          <NavigationItems
            items={hideNavigation ? [] : getRightNavigationItems(showReferral, userEmail)}
            openIndex={openIndex}
            setOpenIndex={setOpenIndex}
          />
          {!loggedIn && (
            <div className={classnames(Styles.menuItem, Styles.menuGroup, HeaderStyles.headerLink)}>
              <div className={classnames(Styles.dropMenu)}>
                <div className={classnames(Styles.label)}>
                  <Link to={LoginRoute} data-cy="header-login-link">
                    <Button
                      className={Styles.profileButton}
                      onClick={() => {
                        const currentURL = new URL(window.location.href);
                        setAfterLoginRedirectURL(currentURL.pathname);
                      }}
                    >
                      <span>{t("navbar.login")}</span>
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          )}
        </>
      </div>
    </>
  );
};

const SubMenu = ({
  item,
  index,
  isVisible,
}: {
  item: menuItemI;
  index: number;
  isVisible: (hide?: string[]) => boolean;
}) => {
  const { navbar } = useTheme();
  const { t } = useTranslation();

  return (
    <ul className={classnames(navbar.subMenu)}>
      {item.children?.map(
        (child, childIndex) =>
          isVisible(child.hide) && (
            <li key={`menuItem_${index}_${childIndex}`} className={navbar.item}>
              {child.label === "logout" ? (
                <Link to={LogoutRoute} className={classnames(HeaderStyles.headerLink)}>
                  {t("navbar.myProfile.logout")}
                </Link>
              ) : (
                // eslint-disable-next-line
                <Link className={classnames(HeaderStyles.headerLink)} to={child.url!}>
                  {child.label}
                </Link>
              )}
            </li>
          )
      )}
    </ul>
  );
};

export default Navigation;
